<script lang="ts">
  import { worldStore, dispatch } from '@sim/store';
  import { activeBuilding } from '@sim/world';
  import { money, defenseSummary } from '@sim/selectors';
  import { roomDef } from '@sim/config';

  $: city = $worldStore;
  $: b = activeBuilding(city)!;
  $: raid = b.raid;
  $: left = raid ? Math.max(0, raid.resolveAt - city.t) : 0;
  $: def = defenseSummary(b, city.t);
  $: strength = raid ? Math.round(b.layingLow ? raid.strength * 0.5 : raid.strength) : 0;
  $: winning = def.defense >= strength;
  $: canBribe = raid ? city.cash >= raid.bribeCost : false;
  $: bracedId = raid?.bracedRoomId ?? null;

  function brace(roomId: number) {
    dispatch({ type: 'braceRoom', roomId });
  }
</script>

{#if raid}
  <div class="overlay">
    <div class="modal">
      <div class="siren">🚨 POLICE RAID 🚨</div>
      <p class="count">{b.name} · hitting in <b class="mono">{left.toFixed(1)}s</b></p>

      <div class="bars">
        <div class="b">
          <span>Their force</span>
          <b class="mono" style="color:var(--bad)">{strength}</b>
        </div>
        <div class="vs">vs</div>
        <div class="b">
          <span>Your defense</span>
          <b class="mono" style="color:var(--heat-cool)">{def.defense}</b>
        </div>
      </div>
      <div class="verdict" style="color:{winning ? 'var(--good)' : 'var(--bad)'}">
        {winning ? '🛡️ You can hold them off' : '⚠️ They’ll break through — prep now'}
      </div>

      <div class="bracelbl">Brace a room — shields it (and whoever's working there), +15% defense:</div>
      <div class="braceopts">
        {#each b.rooms as room (room.id)}
          {@const rdef = roomDef(room.type)}
          <button class="brace" class:on={bracedId === room.id} on:click={() => brace(room.id)}>
            <span class="bi">{rdef.icon}</span>{rdef.name}
          </button>
        {/each}
      </div>

      <div class="choices">
        <button class="lay" class:on={b.layingLow} on:click={() => dispatch({ type: 'toggleLayLow' })}>
          {b.layingLow ? '❄️ Lying Low (force halved)' : '❄️ Lay Low (halve their force)'}
        </button>
        <button class="bribe" disabled={!canBribe} on:click={() => dispatch({ type: 'bribeRaid' })}>
          💵 Bribe out {money(raid.bribeCost)}
        </button>
      </div>
      <p class="tip">The crew (Combat skill + Lookouts) defends automatically. Stash rooms hide a cut of product & cash.</p>
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: absolute;
    inset: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(6, 6, 12, 0.55);
    animation: siren 0.9s ease-in-out infinite;
  }
  .modal {
    width: 100%;
    max-width: 360px;
    background: var(--panel);
    border: 1px solid var(--heat-crit);
    border-radius: 14px;
    padding: 16px;
    box-shadow: 0 0 40px rgba(255, 59, 92, 0.4);
    animation: pop 0.2s ease both;
  }
  .siren {
    text-align: center;
    font-size: 18px;
    font-weight: 900;
    color: var(--heat-crit);
    animation: pulse 0.7s ease-in-out infinite;
  }
  .count {
    text-align: center;
    font-size: 13px;
    color: var(--ink-dim);
    margin: 6px 0 12px;
  }
  .bars {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-bottom: 8px;
  }
  .b {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .b span {
    font-size: 10px;
    color: var(--ink-faint);
  }
  .b b {
    font-size: 22px;
  }
  .vs {
    color: var(--ink-faint);
    font-size: 11px;
  }
  .verdict {
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 12px;
  }
  .bracelbl {
    font-size: 10.5px;
    color: var(--ink-faint);
    text-align: center;
    margin-bottom: 6px;
  }
  .braceopts {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
    margin-bottom: 12px;
  }
  .brace {
    font-size: 11px;
    font-weight: 700;
    padding: 6px 9px;
    border-radius: 8px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink-dim);
  }
  .bi {
    margin-right: 3px;
  }
  .brace.on {
    border-color: var(--heat-cool);
    color: var(--heat-cool);
    background: color-mix(in srgb, var(--heat-cool) 16%, var(--panel-2));
  }
  .choices {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .lay,
  .bribe {
    padding: 11px;
    border-radius: 9px;
    font-weight: 800;
    font-size: 13px;
    border: 1px solid var(--line);
    background: var(--panel-2);
    color: var(--ink);
  }
  .lay {
    border-color: var(--heat-cool);
    color: var(--heat-cool);
  }
  .lay.on {
    background: color-mix(in srgb, var(--heat-cool) 18%, var(--panel-2));
  }
  .bribe {
    border-color: var(--cash);
    color: var(--cash);
  }
  .tip {
    text-align: center;
    font-size: 10px;
    color: var(--ink-faint);
    margin: 10px 0 0;
  }
</style>
